import { forwardRef, InputHTMLAttributes, useId } from 'react';
import { cn } from '@/lib/utils';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, error, id, ...props }, ref) => {
    // The rendered <label> was previously not tied to its input, so clicking it
    // did nothing and screen readers announced the field unlabelled. A generated
    // id fixes that; an explicitly-passed `id` still wins, so callers that
    // already supply one (e.g. ConnectionsTab's "woocommerce-api-key") are
    // unaffected.
    const generatedId = useId();
    const inputId = id ?? generatedId;

    return (
      <div className="space-y-s2">
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium leading-none text-[rgb(var(--text-primary))] peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {label}
          </label>
        )}
        <input
          id={inputId}
          type={type}
          className={cn(
            "flex h-10 w-full rounded-lg border border-[rgb(var(--border-color))] bg-[rgb(var(--bg-secondary))] px-s3 py-s2 text-sm text-[rgb(var(--text-primary))] ring-offset-[rgb(var(--bg-primary))] file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-[rgb(var(--text-muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-primary))] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-error focus-visible:ring-[rgb(var(--accent-primary))]",
            className
          )}
          ref={ref}
          {...props}
        />
        {error && (
          <p className="text-sm text-error">{error}</p>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input };